import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./lib/auth";
import DashboardLayout from "./components/DashboardLayout";
import LoginPage from "./pages/LoginPage";

// Recover from stale chunk hashes after a deploy by reloading once.
function lazyWithRetry<T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const KEY = "fygg-chunk-retry";
    const recover = (err: unknown): never => {
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
      }
      throw err;
    };
    try {
      const mod = await importer();
      if (!mod || typeof mod !== "object" || !("default" in mod) || mod.default == null) {
        return recover(new Error("Lazy module missing default export — stale chunk"));
      }
      sessionStorage.removeItem(KEY);
      return mod;
    } catch (err) {
      return recover(err);
    }
  });
}

// Lazy load pages
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const PipelinePage = lazyWithRetry(() => import("./pages/PipelinePage"));
const DealDetailPage = lazyWithRetry(() => import("./pages/DealDetailPage"));
const AccountsPage = lazyWithRetry(() => import("./pages/AccountsPage"));
const AccountDetailPage = lazyWithRetry(() => import("./pages/AccountDetailPage"));
const ContactsPage = lazyWithRetry(() => import("./pages/ContactsPage"));
const ContactDetailPage = lazyWithRetry(() => import("./pages/ContactDetailPage"));
const TasksPage = lazyWithRetry(() => import("./pages/TasksPage"));
const AnalyticsPage = lazyWithRetry(() => import("./pages/AnalyticsPage"));
const AcademyPage = lazyWithRetry(() => import("./pages/AcademyPage"));
const PlaybookDetailPage = lazyWithRetry(() => import("./pages/PlaybookDetailPage"));
const GoalsPage = lazyWithRetry(() => import("./pages/GoalsPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingFallback />;

  return (
    <React.Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/pipeline" element={<PipelinePage />} />
          <Route path="/deals/:id" element={<DealDetailPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/accounts/:id" element={<AccountDetailPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/:id" element={<ContactDetailPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/academy" element={<AcademyPage />} />
          <Route path="/academy/:customerType" element={<PlaybookDetailPage />} />
          <Route path="/goals" element={<GoalsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings/*" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}
